import { useState, useRef, useId, useEffect } from 'react'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { GripVertical } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import type { EditorFieldDefinition } from '../page-blocks/registry'
import { useArticleSearch, usePagePoolArticles, useArticlesByIds } from '../../api/hooks'
import { useTopics, useRegions } from '../../hooks/useTaxonomy'
import type { BlockContent, PageFeedType, WireArticle, PagePool, PageKind } from '../../api/types'
import type { BlockCanvasState } from '../editor-shared/useBlockCanvas'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const FEED_TYPES: { value: PageFeedType; label: string }[] = [
  { value: 'latest',      label: 'Latest'      },
  { value: 'popular',     label: 'Popular'     },
  { value: 'trending',    label: 'Trending'    },
  { value: 'recommended', label: 'Recommended' },
]

const labelClass = 'text-[10px] font-bold uppercase tracking-widest text-fg-secondary'
const sectionHeaderClass = 'mb-3 border-b border-line pb-1.5 text-[10px] font-bold uppercase tracking-widest text-accent-tertiary-fg'
const inputClass =
  'w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg placeholder:text-fg-secondary focus:border-accent-primary-solid focus:outline-none'
const selectClass =
  'w-full rounded border border-line bg-canvas px-2 py-1.5 text-xs text-fg focus:border-accent-primary-solid focus:outline-none'

// ─── SortableArticleCard ─────────────────────────────────────────────────────

function SortableArticleCard({
  id,
  label,
  article,
  readOnly,
  onRemove,
}: {
  id: string
  label: string | number
  article: WireArticle | undefined
  readOnly: boolean
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const isBadge = typeof label === 'string'
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded border p-1.5 transition-colors ${
        isDragging ? 'opacity-40' : ''
      } ${!readOnly ? 'cursor-grab touch-none active:cursor-grabbing' : ''} ${isBadge ? 'border-info-line bg-info-fill' : 'border-line bg-canvas'}`}
      {...(readOnly ? {} : { ...attributes, ...listeners })}
    >
      {!readOnly && (
        <span aria-hidden="true" className="shrink-0 text-fg-secondary opacity-40">
          <GripVertical size={12} />
        </span>
      )}
      {isBadge ? (
        <span className="shrink-0 rounded-sm bg-info-line px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-white">
          {label}
        </span>
      ) : (
        <span className="w-4 shrink-0 text-center text-[10px] font-bold text-fg-secondary">
          {label}
        </span>
      )}
      {article?.thumbnail?.presignedUrl ? (
        <img src={article.thumbnail.presignedUrl} alt="" aria-hidden="true" className="h-8 w-12 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-8 w-12 shrink-0 rounded bg-fill" aria-hidden="true" />
      )}
      <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
        {article?.title ?? id}
      </p>
      {!readOnly && (
        <button
          type="button"
          aria-label={`Remove ${article?.title ?? id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onRemove}
          className="shrink-0 rounded px-1 text-[10px] text-fg-secondary hover:bg-red-9 hover:text-white"
        >
          ✕
        </button>
      )}
    </li>
  )
}

// ─── StaticContentEditor ──────────────────────────────────────────────────────

function StaticContentEditor({
  content,
  maxArticles,
  featureSplit,
  onChange,
  readOnly,
}: {
  content: BlockContent
  maxArticles: number
  featureSplit?: number
  onChange: (c: BlockContent) => void
  readOnly: boolean
}) {
  const ids = content.articleIds ?? []
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [activeId, setActiveId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  // Resolve existing IDs → published articles for display
  const { data: resolvedArticles = [] } = useArticlesByIds(ids)
  const resolvedById: Record<string, WireArticle> = Object.fromEntries(
    resolvedArticles.map((a) => [a.id, a])
  )

  // Search results (debounced inside the hook)
  const { data: searchResults = [], isFetching: isSearching } = useArticleSearch(query)
  const filteredResults = searchResults.filter((a) => !ids.includes(a.id))
  const showResults = open && query.trim().length >= 2

  function addArticle(id: string) {
    onChange({ ...content, articleIds: [...ids, id] })
    setQuery('')
    setOpen(false)
    setActiveIdx(-1)
    inputRef.current?.focus()
  }

  function removeId(i: number) {
    onChange({ ...content, articleIds: ids.filter((_, j) => j !== i) })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIdx((i) => Math.min(i + 1, filteredResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIdx >= 0 && filteredResults[activeIdx]) {
        addArticle(filteredResults[activeIdx].id)
      } else if (query.trim()) {
        // Paste raw ID directly
        addArticle(query.trim())
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setActiveIdx(-1)
    }
  }

  const splitAt = featureSplit ?? 0
  const isFeatureRail = featureSplit !== undefined
  const featureIds = isFeatureRail ? ids.slice(0, splitAt) : []
  const railIds = isFeatureRail ? ids.slice(splitAt) : []
  const maxRail = isFeatureRail ? maxArticles - splitAt : 0
  const featureSlotFilled = isFeatureRail && ids.length >= splitAt && splitAt > 0 && ids.length > 0
  const canAddMore = !readOnly && ids.length < maxArticles

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex !== -1 && newIndex !== -1) {
      onChange({ ...content, articleIds: arrayMove(ids, oldIndex, newIndex) })
    }
  }

  const activeArticle = activeId ? (resolvedById[activeId] as WireArticle | undefined) : undefined

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
    <div className="space-y-2">
      {/* Article sections */}
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {isFeatureRail ? (
          <div className="space-y-3">
            {/* Feature Article */}
            <div>
              <div className={`mb-1.5 ${labelClass}`}>Feature Article</div>
              {featureIds.length > 0 ? (
                <ul className="space-y-1.5">
                  {featureIds.map((id, i) => (
                    <SortableArticleCard
                      key={id}
                      id={id}
                      label="Feature"
                      article={resolvedById[id]}
                      readOnly={readOnly}
                      onRemove={() => removeId(i)}
                    />
                  ))}
                </ul>
              ) : readOnly ? (
                <p className="text-[11px] italic text-fg-secondary opacity-60">No feature article assigned.</p>
              ) : (
                <p className="rounded border border-dashed border-info-line bg-info-fill px-2.5 py-2 text-[11px] italic text-info-fg">
                  Add the main feature article using the search below.
                </p>
              )}
            </div>

            {/* Rail Articles — only shown once feature is assigned */}
            {featureSlotFilled && (
              <div>
                <div className={`mb-1.5 ${labelClass}`}>
                  Rail Articles{' '}
                  <span className="font-normal normal-case opacity-60">— up to {maxRail}</span>
                </div>
                {railIds.length > 0 ? (
                  <ul className="space-y-1.5">
                    {railIds.map((id, i) => (
                      <SortableArticleCard
                        key={id}
                        id={id}
                        label={i + 1}
                        article={resolvedById[id]}
                        readOnly={readOnly}
                        onRemove={() => removeId(i + splitAt)}
                      />
                    ))}
                  </ul>
                ) : readOnly ? (
                  <p className="text-[11px] italic text-fg-secondary opacity-60">No rail articles assigned.</p>
                ) : (
                  <p className="text-[11px] italic text-fg-secondary opacity-60">No rail articles yet.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            {ids.length > 0 && (
              <ul className="space-y-1.5">
                {ids.map((id, i) => (
                  <SortableArticleCard
                    key={id}
                    id={id}
                    label={i + 1}
                    article={resolvedById[id]}
                    readOnly={readOnly}
                    onRemove={() => removeId(i)}
                  />
                ))}
              </ul>
            )}
            {ids.length === 0 && readOnly && (
              <p className="text-[11px] italic text-fg-secondary opacity-60">No articles assigned.</p>
            )}
          </>
        )}
      </SortableContext>

      {/* Search combobox */}
      {canAddMore && (
        <div className="relative">
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded={showResults}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={
              activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
              setActiveIdx(-1)
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={
              isFeatureRail && featureIds.length === 0
                ? 'Add feature article…'
                : 'Search by title or paste ID…'
            }
            className={inputClass}
          />
          {isSearching && (
            <span
              aria-hidden="true"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-fg-secondary opacity-60"
            >
              …
            </span>
          )}
          {showResults && filteredResults.length > 0 && (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-y-auto rounded border border-line bg-canvas shadow-lg"
            >
              {filteredResults.map((a, i) => (
                <li
                  key={a.id}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={i === activeIdx}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    addArticle(a.id)
                  }}
                  className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[11px] transition-colors ${
                    i === activeIdx
                      ? 'bg-accent-primary-fill text-accent-primary-fg'
                      : 'hover:bg-fill'
                  }`}
                >
                  {a.thumbnail?.presignedUrl && (
                    <img
                      src={a.thumbnail.presignedUrl}
                      alt=""
                      aria-hidden="true"
                      className="h-7 w-10 shrink-0 rounded object-cover"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="truncate font-medium text-fg">{a.title}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="text-[10px] text-fg-secondary opacity-50">
        {isFeatureRail
          ? `${featureIds.length}/1 feature · ${railIds.length}/${maxRail} rail`
          : `${ids.length} / ${maxArticles} article${maxArticles !== 1 ? 's' : ''}`}
      </p>
    </div>

    <DragOverlay dropAnimation={null}>
      {activeId && (
        <li className="flex items-center gap-2 rounded border border-line bg-canvas p-1.5 shadow-lg list-none">
          {activeArticle?.thumbnail?.presignedUrl ? (
            <img src={activeArticle.thumbnail.presignedUrl} alt="" aria-hidden="true" className="h-8 w-12 shrink-0 rounded object-cover" />
          ) : (
            <div className="h-8 w-12 shrink-0 rounded bg-fill" aria-hidden="true" />
          )}
          <p className="min-w-0 flex-1 truncate text-[11px] font-medium text-fg">
            {activeArticle?.title ?? activeId}
          </p>
        </li>
      )}
    </DragOverlay>
    </DndContext>
  )
}

// ─── SlotConfigEditor ────────────────────────────────────────────────────────

function SlotConfigEditor({
  fields,
  config,
  onChange,
  readOnly,
}: {
  fields: EditorFieldDefinition[]
  config: Record<string, string | number | boolean | null>
  onChange: (key: string, value: string | number | boolean | null) => void
  readOnly: boolean
}) {
  if (fields.length === 0) return null
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const value = config[field.key] ?? ''
        if (field.type === 'toggle') {
          return (
            <div key={field.key} className="flex items-center justify-between">
              <label className={labelClass}>{field.label}</label>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(value)}
                disabled={readOnly}
                onClick={() => onChange(field.key, !value)}
                className={`relative h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary-solid ${
                  value ? 'bg-accent-primary-solid' : 'bg-line-strong'
                } disabled:opacity-50`}
              >
                <span
                  className={`absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    value ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          )
        }
        if (field.type === 'select' && field.options) {
          return (
            <div key={field.key}>
              <label className={`mb-1.5 block ${labelClass}`}>{field.label}</label>
              <select
                value={String(value)}
                disabled={readOnly}
                onChange={(e) => onChange(field.key, e.target.value)}
                className={selectClass}
              >
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )
        }
        if (field.type === 'text' || field.type === 'textarea') {
          const Tag = field.type === 'textarea' ? 'textarea' : 'input'
          return (
            <div key={field.key}>
              <label className={`mb-1.5 block ${labelClass}`}>{field.label}</label>
              <Tag
                value={String(value)}
                disabled={readOnly}
                onChange={(e) => onChange(field.key, e.target.value || null)}
                placeholder={`Enter ${field.label.toLowerCase()}…`}
                className={inputClass}
                {...(field.type === 'textarea' ? { rows: 2 } : {})}
              />
            </div>
          )
        }
        return null
      })}
    </div>
  )
}

// ─── FeedContentEditor ────────────────────────────────────────────────────────

function FeedContentEditor({
  content,
  onChange,
  readOnly,
}: {
  content: BlockContent
  onChange: (c: BlockContent) => void
  readOnly: boolean
}) {
  const params = content.feedParams ?? {}

  function setFeedType(v: PageFeedType) {
    onChange({ ...content, feedType: v })
  }

  function setParam(key: string, val: string | number | undefined) {
    onChange({ ...content, feedParams: { ...params, [key]: val || undefined } })
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={`mb-1.5 block ${labelClass}`}>Feed type</label>
        <select
          value={content.feedType ?? 'latest'}
          disabled={readOnly}
          onChange={(e) => setFeedType(e.target.value as PageFeedType)}
          className={selectClass}
        >
          {FEED_TYPES.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={`mb-1.5 block ${labelClass}`}>Limit</label>
        <input
          type="number"
          min={1}
          max={24}
          placeholder="e.g. 5"
          value={params.limit ?? ''}
          disabled={readOnly}
          onChange={(e) => setParam('limit', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={`mb-1.5 block ${labelClass}`}>Topic slug <span className="normal-case font-normal opacity-60">(optional)</span></label>
        <input
          type="text"
          placeholder="e.g. politics"
          value={params.topicSlug ?? ''}
          disabled={readOnly}
          onChange={(e) => setParam('topicSlug', e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={`mb-1.5 block ${labelClass}`}>Region slug <span className="normal-case font-normal opacity-60">(optional)</span></label>
        <input
          type="text"
          placeholder="e.g. us-west"
          value={params.regionSlug ?? ''}
          disabled={readOnly}
          onChange={(e) => setParam('regionSlug', e.target.value)}
          className={inputClass}
        />
      </div>
    </div>
  )
}

// ─── PoolConfigEditor ─────────────────────────────────────────────────────────

function PoolConfigEditor({
  pool,
  onChange,
  readOnly,
  pageKind,
}: {
  pool: PagePool | undefined
  onChange: (p: PagePool) => void
  readOnly: boolean
  pageKind?: PageKind
}) {
  const current: PagePool = pool ?? {}
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [testActive, setTestActive] = useState(false)
  const isSectionPage = pageKind === 'topic' || pageKind === 'region'
  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()

  // Build a preview pool: for section pages, topic/region are implicit so we
  // only pass query here. The real runtime merges slug at page-load time.
  const previewPool: PagePool | undefined = testActive
    ? { ...current }
    : undefined
  const { articles: previewArticles, isPending: previewPending } = usePagePoolArticles(previewPool)

  function set(key: keyof PagePool, val: string | number | undefined) {
    onChange({ ...current, [key]: val || undefined })
  }

  return (
    <div className="space-y-3">
      {!isSectionPage && (
        <>
          <div>
            <label className={`mb-1.5 block ${labelClass}`}>Topic <span className="normal-case font-normal opacity-60">(optional)</span></label>
            <select
              value={current.topicSlug ?? ''}
              disabled={readOnly}
              onChange={(e) => set('topicSlug', e.target.value)}
              className={selectClass}
            >
              <option value="">Any topic</option>
              {topics.map((t) => (
                <option key={t.slug} value={t.slug}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={`mb-1.5 block ${labelClass}`}>Region <span className="normal-case font-normal opacity-60">(optional)</span></label>
            <select
              value={current.regionSlug ?? ''}
              disabled={readOnly}
              onChange={(e) => set('regionSlug', e.target.value)}
              className={selectClass}
            >
              <option value="">Any region</option>
              {regions.map((r) => (
                <option key={r.slug} value={r.slug}>{r.label}</option>
              ))}
            </select>
          </div>
        </>
      )}
      <div>
        <label className={`mb-1.5 block ${labelClass}`}>Max articles</label>
        <input
          type="number"
          min={1}
          max={100}
          placeholder="e.g. 30"
          value={current.maxResults ?? ''}
          disabled={readOnly}
          onChange={(e) => set('maxResults', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          className={inputClass}
        />
      </div>

      {/* Advanced */}
      <div>
        <button
          type="button"
          onClick={() => { setShowAdvanced((v) => !v); setTestActive(false) }}
          className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-fg-secondary hover:text-fg"
        >
          <span>{showAdvanced ? '▾' : '▸'}</span> Advanced
        </button>
        {showAdvanced && (
          <div className="mt-3 space-y-3">
            <div>
              <label className={`mb-1.5 block ${labelClass}`}>Keyword filter <span className="normal-case font-normal opacity-60">(optional)</span></label>
              <p className="mb-1.5 text-[10px] text-fg-secondary leading-4">Only include articles containing these words.</p>
              <input
                type="text"
                placeholder="e.g. sanctions summit"
                value={current.query ?? ''}
                disabled={readOnly}
                onChange={(e) => { set('query', e.target.value); setTestActive(false) }}
                className={inputClass}
              />
            </div>
            <button
              type="button"
              disabled={readOnly}
              onClick={() => setTestActive((v) => !v)}
              className="rounded border border-line bg-canvas px-2.5 py-1 text-[11px] font-medium text-fg transition-colors hover:bg-fill disabled:opacity-40"
            >
              {testActive ? 'Hide preview' : 'Test filter'}
            </button>
            {testActive && (
              <div className="rounded border border-line bg-canvas p-2">
                {previewPending ? (
                  <p className="text-[11px] text-fg-secondary">Searching…</p>
                ) : previewArticles.length === 0 ? (
                  <p className="text-[11px] italic text-fg-secondary">No articles matched.</p>
                ) : (
                  <ul className="space-y-1">
                    {previewArticles.slice(0, 8).map((a) => (
                      <li key={a.id} className="truncate text-[11px] text-fg">{a.title}</li>
                    ))}
                    {previewArticles.length > 8 && (
                      <li className="text-[10px] text-fg-secondary">+{previewArticles.length - 8} more</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="text-[10px] text-fg-secondary opacity-60 leading-4">
        Articles matching this pool are distributed to blocks with source&nbsp;=&nbsp;Pool at render time.
      </p>
    </div>
  )
}

// ─── ContentSourceEditor ─────────────────────────────────────────────────────

interface ContentSourceEditorProps {
  content: BlockContent | undefined
  maxArticles: number
  featureSplit?: number
  readOnly: boolean
  onChange: (c: BlockContent) => void
  pageKind?: PageKind
}

function ContentSourceEditor({ content, maxArticles, featureSplit, readOnly, onChange, pageKind }: ContentSourceEditorProps) {
  const sourceLabelId = useId()
  // Pool is only valid for topic/region pages. Front-page and edition content
  // is always hand-picked (static) or feed-driven — never pool-distributed.
  const hidePool = pageKind === 'front-page' || pageKind === 'edition'
  // Topic/region blocks always use pool — source is not user-configurable.
  const forcePool = pageKind === 'topic' || pageKind === 'region'
  const availableSources = (['static', 'feed', 'pool'] as const).filter(
    (s) => !(hidePool && s === 'pool'),
  )

  function setSource(source: 'static' | 'feed' | 'pool') {
    const base: BlockContent =
      source === 'static'
        ? { source: 'static', articleIds: [] }
        : source === 'pool'
        ? { source: 'pool' }
        : { source: 'feed', feedType: 'latest' }
    onChange(base)
  }

  // Auto-correct legacy blocks on topic/region pages: ensure source is 'pool'.
  useEffect(() => {
    if (forcePool && content?.source !== 'pool') {
      onChange({ source: 'pool' })
    }
  }, [forcePool]) // eslint-disable-line react-hooks/exhaustive-deps

  if (forcePool) {
    return (
      <div>
        <div className={`mt-4 ${sectionHeaderClass}`}>Source</div>
        <p className="text-[11px] text-fg-secondary">
          Pool — articles drawn from the page pool at render time.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <div id={sourceLabelId} className={`mt-4 ${sectionHeaderClass}`}>Source</div>
        <ToggleGroup.Root
          type="single"
          value={content?.source ?? ''}
          disabled={readOnly}
          aria-labelledby={sourceLabelId}
          onValueChange={(val) => {
            if (!val) return
            setSource(val as 'static' | 'feed' | 'pool')
          }}
          className="flex overflow-hidden rounded border border-line bg-canvas"
        >
          {availableSources.map((src, i) => (
            <ToggleGroup.Item
              key={src}
              value={src}
              onClick={() => { if (content?.source !== src) setSource(src) }}
              className={[
                'flex-1 py-1.5 text-[11px] font-semibold transition-colors',
                i > 0 ? 'border-l border-line' : '',
                'text-fg-secondary hover:bg-fill hover:text-fg',
                'data-[state=on]:bg-accent-primary-fill data-[state=on]:text-accent-primary-fg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-primary-solid',
                'disabled:cursor-default disabled:opacity-50',
              ].join(' ')}
            >
              {src === 'static' ? 'Static' : src === 'feed' ? 'Feed' : 'Pool'}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
        <p className="mt-1.5 text-[10px] leading-4 text-fg-secondary opacity-70">
          {!content
            ? hidePool
              ? 'Choose whether to use hand-picked articles or a live feed.'
              : 'Choose whether to use hand-picked articles, a live feed, or the page pool.'
            : content.source === 'static'
            ? 'Hand-pick specific articles by ID.'
            : content.source === 'pool'
            ? 'Articles drawn from the page-level pool at render time.'
            : 'Pull articles from a live feed at render time.'}
        </p>
      </div>

      {content && <hr className="border-line" />}

      {content?.source === 'static' && (
        <StaticContentEditor
          content={content}
          maxArticles={maxArticles}
          featureSplit={featureSplit}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}
      {content?.source === 'feed' && (
        <FeedContentEditor
          content={content}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}
      {content?.source === 'pool' && (
        <p className="text-[11px] italic text-fg-secondary opacity-70">
          Configure the pool in the Pool Configuration panel below.
        </p>
      )}
    </div>
  )
}

// ─── PageRightShelf ───────────────────────────────────────────────────────────

interface PageRightShelfProps {
  canvas: BlockCanvasState
  contentMap: Record<string, BlockContent>
  onSetContent: (slotId: string, content: BlockContent) => void
  readOnly?: boolean
  pageKind?: PageKind
  pool?: PagePool
  onPoolChange?: (pool: PagePool) => void
}

export function PageRightShelf({ canvas, contentMap, onSetContent, readOnly = false, pageKind, pool, onPoolChange }: PageRightShelfProps) {
  const { selectedSlot, updateSlotConfig, selectedColKey, selectedColBlockId, setSelectedColKey, setSelectedColBlockId, setSelectedColumnType } = canvas

  // Show pool config panel for topic/region pages, or when any block uses pool source.
  // Never show for front-page or edition (these page kinds don't use pool-driven content).
  const hasPoolBlocks = Object.values(contentMap).some((c) => c?.source === 'pool')
  // Pool config is auto-derived from slug for topic/region pages; never show to editors.
  const showPoolPanel = pageKind !== 'front-page' && pageKind !== 'edition' && pageKind !== 'topic' && pageKind !== 'region' && hasPoolBlocks

  if (!selectedSlot) {
    return (
      <aside className="w-72 shrink-0 overflow-y-auto border-l border-line bg-shelf p-4">
        <h2 className={sectionHeaderClass}>Content</h2>
        <p className="text-xs text-fg-secondary">Select a block in the canvas to assign content.</p>
        {showPoolPanel && onPoolChange && (
          <>
            <hr className="my-4 border-line" />
            <h2 className={sectionHeaderClass}>Pool Configuration</h2>
            <PoolConfigEditor pool={pool} onChange={onPoolChange} readOnly={readOnly} pageKind={pageKind} />
          </>
        )}
      </aside>
    )
  }

  const slot = selectedSlot
  const def = PAGE_BLOCK_REGISTRY[slot.type]
  const isEditorial = def.category === 'editorial'
  const content = contentMap[slot.id]

  return (
    <aside className="w-72 shrink-0 overflow-y-auto border-l border-line bg-shelf p-4">
      {(() => {
        // Editorial-grid with a column block selected — switch context to the column block
        if (slot.type === 'editorial-grid' && selectedColKey && selectedColBlockId) {
          const colBlockType = slot.columnBlocks?.[selectedColKey]?.find((b) => b.id === selectedColBlockId)?.type ?? null
          if (colBlockType) {
            const colDef = PAGE_BLOCK_REGISTRY[colBlockType]
            const colContentKey = `${slot.id}:${selectedColKey}:${selectedColBlockId}`
            return (
              <>
                <h2 className={sectionHeaderClass}>Content</h2>
                <button
                  type="button"
                  onClick={() => { setSelectedColKey(null); setSelectedColBlockId(null); setSelectedColumnType(null) }}
                  className="mb-3 text-[11px] text-fg-secondary underline hover:text-fg"
                >
                  ← Back to row
                </button>
                <p className="mb-4 text-[11px] text-fg-secondary">{colDef.label}</p>
                {colDef.editorFields.length > 0 && (() => {
                  // Build a per-column view of config: keys prefixed with `{colKey}:` are scoped to this column.
                  const prefix = `${selectedColKey}:`
                  const colConfig: Record<string, string | number | boolean | null> = {
                    ...colDef.defaultConfig,
                    ...Object.fromEntries(
                      Object.entries(slot.config)
                        .filter(([k]) => k.startsWith(prefix))
                        .map(([k, v]) => [k.slice(prefix.length), v]),
                    ),
                  }
                  return (
                    <>
                      <div className={sectionHeaderClass}>Display Options</div>
                      <SlotConfigEditor
                        fields={colDef.editorFields}
                        config={colConfig}
                        onChange={(key, val) => updateSlotConfig(slot.id, `${selectedColKey}:${key}`, val)}
                        readOnly={readOnly}
                      />
                    </>
                  )
                })()}
                <ContentSourceEditor
                  content={contentMap[colContentKey]}
                  maxArticles={colDef.maxArticles}
                  featureSplit={colBlockType === 'feature-with-rail' ? 1 : undefined}
                  readOnly={readOnly}
                  onChange={(c) => onSetContent(colContentKey, c)}
                  pageKind={pageKind}
                />
              </>
            )
          }
        }

        // Editorial-grid with no column selected — show row-level options
        if (slot.type === 'editorial-grid') {
          return (
            <>
              <h2 className={sectionHeaderClass}>Content</h2>
              <p className="mb-4 text-[11px] text-fg-secondary">{def.label}</p>
              {def.editorFields.length > 0 && (
                <>
                  <div className={sectionHeaderClass}>Display Options</div>
                  <SlotConfigEditor
                    fields={def.editorFields}
                    config={slot.config}
                    onChange={(key, val) => updateSlotConfig(slot.id, key, val)}
                    readOnly={readOnly}
                  />
                </>
              )}
              <p className="text-xs italic text-fg-secondary opacity-60">
                Content is assigned per column. Click a column block in the canvas.
              </p>
            </>
          )
        }

        // Structural blocks
        if (!isEditorial) {
          return (
            <>
              <h2 className={sectionHeaderClass}>Content</h2>
              <p className="mb-4 text-[11px] text-fg-secondary">{def.label}</p>
              <p className="text-xs italic text-fg-secondary opacity-60">
                No content assignment for structural blocks.
              </p>
            </>
          )
        }

        // Normal editorial block (three-up-row, ranked-list, etc.)
        return (
          <>
            <h2 className={sectionHeaderClass}>Content</h2>
            <p className="mb-4 text-[11px] text-fg-secondary">{def.label}</p>
            {def.editorFields.length > 0 && (
              <>
                <div className={sectionHeaderClass}>Display Options</div>
                <SlotConfigEditor
                  fields={def.editorFields}
                  config={slot.config}
                  onChange={(key, val) => updateSlotConfig(slot.id, key, val)}
                  readOnly={readOnly}
                />
              </>
            )}
            <ContentSourceEditor
              content={content}
              maxArticles={def.maxArticles}
              featureSplit={slot.type === 'feature-with-rail' ? 1 : undefined}
              readOnly={readOnly}
              onChange={(c) => onSetContent(slot.id, c)}
              pageKind={pageKind}
            />
          </>
        )
      })()}
      {showPoolPanel && onPoolChange && (
        <>
          <hr className="my-4 border-line" />
          <h2 className={sectionHeaderClass}>Pool Configuration</h2>
          <PoolConfigEditor pool={pool} onChange={onPoolChange} readOnly={readOnly} pageKind={pageKind} />
        </>
      )}
    </aside>
  )
}
